/** Helpers for admin text – safe on server and client. */

/*
 * Named entities the admin editor writes (Slovak and Czech letters, typographic punctuation). Numeric
 * entities are decoded generally; an unknown name stays as typed.
 */
const ENTITIES: Record<string, string> = {
  nbsp: '\u00A0',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  aacute: 'á',
  Aacute: 'Á',
  auml: 'ä',
  Auml: 'Ä',
  ccaron: 'č',
  Ccaron: 'Č',
  dcaron: 'ď',
  Dcaron: 'Ď',
  eacute: 'é',
  Eacute: 'É',
  ecaron: 'ě',
  Ecaron: 'Ě',
  iacute: 'í',
  Iacute: 'Í',
  lacute: 'ĺ',
  Lacute: 'Ĺ',
  lcaron: 'ľ',
  Lcaron: 'Ľ',
  ncaron: 'ň',
  Ncaron: 'Ň',
  oacute: 'ó',
  Oacute: 'Ó',
  ocirc: 'ô',
  Ocirc: 'Ô',
  ouml: 'ö',
  Ouml: 'Ö',
  racute: 'ŕ',
  Racute: 'Ŕ',
  rcaron: 'ř',
  Rcaron: 'Ř',
  scaron: 'š',
  Scaron: 'Š',
  tcaron: 'ť',
  Tcaron: 'Ť',
  uacute: 'ú',
  Uacute: 'Ú',
  uring: 'ů',
  Uring: 'Ů',
  uuml: 'ü',
  Uuml: 'Ü',
  yacute: 'ý',
  Yacute: 'Ý',
  zcaron: 'ž',
  Zcaron: 'Ž',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  laquo: '«',
  raquo: '»',
  bdquo: '„',
  ldquo: '“',
  rdquo: '”',
  sbquo: '‚',
  lsquo: '‘',
  rsquo: '’',
  middot: '·',
  bull: '•',
  times: '×',
  deg: '°',
  euro: '€',
  copy: '©',
  reg: '®',
  trade: '™',
};

function decodeEntities(text: string) {
  return text.replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (match, name: string) => {
    if (name[0] !== '#') return ENTITIES[name] ?? match;
    const code =
      name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : Number(name.slice(1));
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
  });
}

/** Plain text of admin HTML (labels, alt texts, aria labels); no-break spaces become spaces. */
export function stripTags(html?: string) {
  return html
    ? decodeEntities(html.replace(/<[^>]*>/g, ' '))
        .replace(/\s+/g, ' ')
        .trim()
    : '';
}

/**
 * Admin links are typed by hand: trim them, complete a bare domain („www.heureka.sk“) to https and
 * add the leading slash to internal paths („kontakt“, „hp-laserjet-m110w“).
 */
export function cleanHref(href: string) {
  const value = stripTags(href);
  if (!value) return '';
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(value)) return value;
  if (/^(www\.)?[a-z\d-]+(\.[a-z\d-]+)*\.[a-z]{2,}([/?#]|$)/i.test(value))
    return `https://${value}`;
  return `/${value}`;
}

export function phoneHref(phone: string) {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

/** A number keeps its unit on its line („100 %“, „70 %“, „5 €“) – admin texts are typed with plain spaces. */
export function bindUnits(text: string) {
  return text.replace(/(\d) (?=[%€])/g, '$1\u00A0');
}

/** Links of admin HTML (consents) open in a new tab, so a half-filled form stays as it is. */
export function withNewTabLinks(html: string) {
  return html.replace(/<a\s(?![^>]*\btarget=)/gi, '<a target="_blank" rel="noopener" ');
}

/** External http(s) link – opens in a new tab. */
export function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href);
}

/** `{name}` placeholders typed in the administration („Zavolať: {telefon}“, „© {rok} …“). */
export function fillTemplate(text: string, values: Record<string, string | number>) {
  return text.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

/** Options typed into one admin field, separated by „ | “ („Laserovú | Atramentovú | Neviem“). */
export function splitOptions(text: string) {
  return stripTags(text)
    .split('|')
    .map(option => option.trim())
    .filter(Boolean);
}

/**
 * Plural forms typed into one admin field as „one | few | many“ („{pocet} produkt | {pocet} produkty |
 * {pocet} produktov“), picked by the Slovak plural rules and filled with `values`. A field with one
 * form is used for every count.
 */
export function pluralText(
  text: string,
  count: number,
  values: Record<string, string | number> = {},
  locale = 'sk',
) {
  const forms = text.split('|').map(form => form.trim());
  const category = new Intl.PluralRules(locale).select(count);
  const form = category === 'one' ? forms[0] : category === 'few' ? forms[1] : forms[2];
  return fillTemplate(form ?? forms[forms.length - 1] ?? '', { pocet: count, ...values });
}

export type LinkData = { label: string; href: string };
